import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { bgTasksApi } from "@/features/bg_tasks/api/bgTasks";
import { useAccessGroups } from "@/features/repository/hooks/useRepository";

import { twoDriveApi, type ShareCreatePayload } from "./api";

const qk = {
  nodes: (parent: string | null) => ["two-drive", "nodes", parent ?? "root"] as const,
  shares: (nodeId: string) => ["two-drive", "shares", nodeId] as const,
  sharedWithMe: ["two-drive", "shared-with-me"] as const,
  sharedByMe: ["two-drive", "shared-by-me"] as const,
  quota: ["two-drive", "quota"] as const,
  users: ["two-drive", "users"] as const,
  zipTask: (id: string) => ["two-drive", "zip-task", id] as const,
  gisPreview: (id: string | null) => ["two-drive", "gis-preview", id] as const,
};

// The preview's WMS URL is signed for 1 h; refetch 10 min early (same as useSignedWmsUrl).
const GIS_PREVIEW_REFRESH_MS = 50 * 60 * 1000;

export function useNodeList(parent: string | null) {
  return useQuery({
    queryKey: qk.nodes(parent),
    queryFn: () => twoDriveApi.listNodes(parent),
  });
}

export function useQuota() {
  return useQuery({ queryKey: qk.quota, queryFn: twoDriveApi.quota });
}

/** Konta, którym można udostępnić plik i którym CEO ustawia limit. */
export function useDriveUsers() {
  return useQuery({
    queryKey: qk.users,
    queryFn: twoDriveApi.listUsers,
    staleTime: 5 * 60 * 1000, // 5 min — lista kont zmienia się rzadko
  });
}

export function useSharedWithMe() {
  return useQuery({ queryKey: qk.sharedWithMe, queryFn: twoDriveApi.sharedWithMe });
}

export function useSharedByMe() {
  return useQuery({ queryKey: qk.sharedByMe, queryFn: twoDriveApi.sharedByMe });
}

export function useNodeShares(nodeId: string | null) {
  return useQuery({
    queryKey: qk.shares(nodeId ?? ""),
    queryFn: () => twoDriveApi.listShares(nodeId as string),
    enabled: !!nodeId,
  });
}

// Sharing groups are the same owner-private access groups managed in the
// repository app; reuse its hook so the "Grupy dostępu" modal and the share
// picker share one cache (group edits refresh the picker instantly).
export function useGroups() {
  return useAccessGroups();
}

export function useCreateFolder(parent: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => twoDriveApi.createFolder(name, parent),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.nodes(parent) }),
  });
}

export function useRenameMove(parent: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; parent?: string | null }) =>
      twoDriveApi.renameMove(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.nodes(parent) }),
  });
}

export function useDeleteNode(parent: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => twoDriveApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.nodes(parent) });
      qc.invalidateQueries({ queryKey: qk.quota });
    },
  });
}

// Reject if any of a batch of per-node operations failed, surfacing the first
// error so the caller's onError runs. Used by the bulk move/delete hooks.
async function runBatch<T>(ops: Promise<T>[]) {
  const results = await Promise.allSettled(ops);
  const failed = results.find((r) => r.status === "rejected");
  if (failed && failed.status === "rejected") throw failed.reason;
}

/** Move one or more nodes into `dest` (null = my root). */
export function useMoveNodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, dest }: { ids: string[]; dest: string | null }) =>
      runBatch(ids.map((id) => twoDriveApi.renameMove(id, { parent: dest }))),
    // Source and destination listings both change — refresh every folder view.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["two-drive", "nodes"] }),
  });
}

/** Copy one or more nodes into `dest` (null = my root). */
export function useCopyNodes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, dest }: { ids: string[]; dest: string | null }) =>
      twoDriveApi.copyNodes(ids, dest),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["two-drive", "nodes"] });
      qc.invalidateQueries({ queryKey: qk.quota });
    },
  });
}

/** Delete one or more nodes. */
export function useDeleteNodes(parent: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => runBatch(ids.map((id) => twoDriveApi.remove(id))),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.nodes(parent) });
      qc.invalidateQueries({ queryKey: qk.quota });
    },
  });
}

export function useCreateShare(nodeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ShareCreatePayload) => twoDriveApi.createShare(nodeId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.shares(nodeId) });
      qc.invalidateQueries({ queryKey: qk.sharedByMe });
    },
  });
}

export function useDeleteShare(nodeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shareId: string) => twoDriveApi.deleteShare(shareId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.shares(nodeId) });
      qc.invalidateQueries({ queryKey: qk.sharedByMe });
    },
  });
}

export function useSetQuota() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, quotaBytes }: { userId: string; quotaBytes: number }) =>
      twoDriveApi.setQuota(userId, quotaBytes),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.quota }),
  });
}

/** Poll a "zip a folder" background task until it finishes. */
export function useZipTask(taskId: string | null) {
  return useQuery({
    queryKey: qk.zipTask(taskId ?? ""),
    queryFn: () => bgTasksApi.retrieve(taskId as string),
    enabled: !!taskId,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "pending" || s === "running" ? 2000 : false;
    },
  });
}

/** Map preview of a GeoPackage; the first call builds the QGIS project (a few seconds). */
export function useGisPreview(nodeId: string | null) {
  return useQuery({
    queryKey: qk.gisPreview(nodeId),
    queryFn: () => twoDriveApi.gisPreview(nodeId as string),
    enabled: !!nodeId,
    staleTime: GIS_PREVIEW_REFRESH_MS,
    refetchInterval: GIS_PREVIEW_REFRESH_MS,
    refetchIntervalInBackground: true,
    // A refusal (too big, not a GeoPackage, unscanned) does not change on retry.
    retry: false,
  });
}
